import { format, parseISO, formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";

export const formatDate = (
  date: string | Date,
  pattern: string = "yyyy.MM.dd",
): string => {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return format(dateObj, pattern, { locale: ko });
};

export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: ko });
};

/** "2026.01.01 (토)" — 날짜 + 요일(한글 1자). "YYYY-MM-DD"/ISO 양쪽 입력 허용. */
export const formatDateWithWeekday = (date: string | Date): string =>
  formatDate(date, "yyyy.MM.dd (EEEEE)");

/** "2026.01.01 14:23" — 날짜 + 시:분. ISO 입력. */
export const formatDateTime = (date: string | Date): string =>
  formatDate(date, "yyyy.MM.dd HH:mm");

export const formatNumber = (num: number): string => num.toLocaleString("ko-KR");

export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  }
  return phone;
};

/**
 * 입력 진행용 휴대폰번호 포맷터 — 숫자를 타이핑하는 동안 3-4-4 하이픈을 점진 삽입한다.
 * "010" → "010", "0102" → "010-2", "01023162110" → "010-2316-2110".
 * (완성 시점에만 포맷하는 formatPhoneNumber 와 달리 부분 입력에도 하이픈을 넣는다.)
 * digits-only 입력을 가정하되 방어적으로 비숫자를 제거한다.
 */
export const formatPhoneProgressive = (digits: string): string => {
  const d = digits.replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

/**
 * 생년월일 표시 포맷: "19961010" → "1996.10.10".
 * 비숫자를 먼저 제거하므로 이미 구분자가 있는 입력("1990.01.01")에도 멱등이다.
 * 8자리가 아니면 원본을 그대로 반환한다.
 */
export const formatBirthDate = (birth: string): string => {
  const cleaned = birth.replace(/\D/g, "");
  if (cleaned.length === 8) {
    return cleaned.replace(/(\d{4})(\d{2})(\d{2})/, "$1.$2.$3");
  }
  return birth;
};

export const formatCurrency = (amount: number): string => `${formatNumber(amount)}원`;

/**
 * "1,234 원" — 숫자와 "원" 사이 공백. 수수료 명세 리스트 행(요약 카드·상세 섹션 행) 표기용.
 * 히어로/총 지급액(맨 위 실 지급액)은 공백 없는 formatCurrency 를 쓴다(Figma 4667-55169 / 4816-57326).
 */
export const formatCurrencySpaced = (amount: number): string => `${formatNumber(amount)} 원`;

export const formatPercent = (value: number, decimals: number = 1): string =>
  `${value.toFixed(decimals)}%`;

/**
 * 억/만/원 혼용 금액 포맷 — 보장분석 등 큰 금액을 사람이 읽기 쉬운 단위로 표기할 때 쓴다.
 * 규칙: 억·만·원 세 단위로 나눠 값이 0인 단위는 생략하고, 각 단위 숫자는 천 단위 콤마.
 * 숫자와 단위는 붙여 쓰고("5만원"), 상위 단위와 하위 금액 사이만 한 칸 띄운다("2만 6,000원").
 * 절사·반올림 없이 원 단위까지 그대로 표기한다. 0·음수·NaN·Infinity 는 "0원".
 * 입력은 정수 원 단위 전용(서버 금액·정수 카운트업 프레임). 소수 입력은 정의 밖이며 방어적으로 정수화만 한다.
 *
 * 예:
 * - 6_000         → "6,000원"
 * - 26_000        → "2만 6,000원"
 * - 15_000_000    → "1,500만원"
 * - 15_003_000    → "1,500만 3,000원"
 * - 100_000_000   → "1억원"
 * - 100_003_000   → "1억 3,000원"
 * - 150_003_000   → "1억 5,000만 3,000원"
 * - 0             → "0원"
 */
export const formatWonKorean = (amount: number): string => {
  if (!Number.isFinite(amount) || amount <= 0) return "0원";

  const n = Math.floor(amount);
  const eok = Math.floor(n / 100_000_000);
  const man = Math.floor((n % 100_000_000) / 10_000);
  const won = n % 10_000;

  const parts: string[] = [];
  if (eok > 0) parts.push(`${formatNumber(eok)}억`);
  if (man > 0) parts.push(`${formatNumber(man)}만`);
  if (won > 0) parts.push(formatNumber(won));

  return parts.length === 0 ? "0원" : `${parts.join(" ")}원`;
};
